import React, { useState, useEffect } from "react";
import { useTranslations } from "use-intl";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Loader2 } from "lucide-react";
import ActualSekAmountField from "@/components/shared/ActualSekAmountField";
import { parseActualSekAmount } from "@/components/shared/actualSekAmount";
import { format } from "date-fns";
import { ExpenseReimbursement } from "@/types/expense";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { toast } from "sonner";

interface MarkPaidExternallyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expense: ExpenseReimbursement;
  onComplete: () => void;
}

const MarkPaidExternallyModal: React.FC<MarkPaidExternallyModalProps> = ({
  open,
  onOpenChange,
  expense,
  onComplete,
}) => {
  const t = useTranslations("consultantExpenses.markPaid");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [submitting, setSubmitting] = useState(false);
  const [actualSekAmountInput, setActualSekAmountInput] = useState("");

  useEffect(() => {
    if (open) {
      // Prefill with the expense's current SEK estimate — confirming the default
      // is a single click; the user only edits it when the real amount differed.
      setActualSekAmountInput(expense.sek_amount != null ? String(expense.sek_amount) : "");
      setSelectedDate(new Date());
    } else {
      setActualSekAmountInput("");
    }
  }, [open, expense.id, expense.sek_amount]);

  const isForeignCurrency = (expense.currency || "SEK") !== "SEK";
  const { amount: parsedActualSekAmount } = parseActualSekAmount(actualSekAmountInput);
  const isActualSekAmountValid = parsedActualSekAmount !== null;

  const handleConfirm = async () => {
    if (isForeignCurrency && !isActualSekAmountValid) {
      return;
    }
    setSubmitting(true);
    try {
      const service = ServiceRegistry.getExpenseService();
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      await service.approveAndMarkPaidExternally(
        expense.id,
        formattedDate,
        isForeignCurrency ? parsedActualSekAmount : undefined
      );
      toast.success(t("toast"));
      onComplete();
    } catch (error) {
      console.error("Failed to mark expense as paid externally:", error);
      toast.error((error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("body")}</p>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && setSelectedDate(date)}
            className="rounded-md border"
          />
        </div>

        {isForeignCurrency && (
          <ActualSekAmountField
            id="expense-actual-sek-amount"
            value={actualSekAmountInput}
            onChange={setActualSekAmountInput}
            className="mt-4"
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || (isForeignCurrency && !isActualSekAmountValid)}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t("confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarkPaidExternallyModal;
